/**
 * Builds the iframe URL used to launch a module inside a channel.
 *
 * Mints a short-lived Firebase custom token via /api/mint_module_token,
 * then composes:
 *   <app.launch_url>?app_id=<id>&channel_id=<channel>&token=<custom_token>
 *
 * The module page loads /api.js, which in turn calls
 * signInWithCustomToken(token) and joins channels/{channel}.
 */
import { firebaseAuth } from './firebase';

const API_ROOT = (typeof gatheract !== 'undefined' && gatheract.apiRoot)
	? gatheract.apiRoot
	: `${location.protocol}//${location.host}/api`;

export async function mintModuleToken(channelId: string, appId: string): Promise<string> {
	const user = firebaseAuth.currentUser;
	if (!user) throw new Error('mintModuleToken: not signed in');
	const idToken = await user.getIdToken();
	const resp = await fetch(`${API_ROOT}/mint_module_token`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${idToken}`
		},
		body: JSON.stringify({ channelId, appId })
	});
	if (!resp.ok) {
		throw new Error(`mintModuleToken failed: ${resp.status} ${await resp.text()}`);
	}
	const { token } = await resp.json();
	return token;
}

export async function buildAppLaunchUrl(
	launchUrl: string,
	appId: string,
	channelId: string
): Promise<string> {
	const token = await mintModuleToken(channelId, appId);
	// Fall back to the bundled module directory when no custom launch_url is set.
	const base = launchUrl && launchUrl.trim() ? launchUrl : `/modules/${appId}/`;
	const sep = base.includes('?') ? '&' : '?';
	return `${base}${sep}app_id=${encodeURIComponent(appId)}` +
	       `&channel_id=${encodeURIComponent(channelId)}` +
	       `&token=${encodeURIComponent(token)}`;
}
